import { useEffect, useState, type FormEvent } from 'react'
import {
  Bank,
  Plus,
  PencilSimple,
  X as XIcon,
} from '@phosphor-icons/react'
import { Modal } from '@/components/Modal'
import { supabase } from '@/lib/supabase'
import type { CuentaBancariaPropia } from '@/lib/typesNeoCore'

/**
 * Módulo Bancos — gestión de cuentas bancarias propias de la cooperativa.
 *
 * Estas cuentas son el destino de los depósitos que hacen los asociados
 * en banco, y la fuente de las transferencias bancarias que hace la coop.
 * Hoy Neocoop tiene 2 cuentas en Banco Internacional pero el modelo
 * soporta N bancos / N cuentas.
 *
 * v0 alcance:
 *   - Listar todas las cuentas
 *   - Agregar / editar
 *   - Activar / desactivar (no se borran — afectan integridad de boletas)
 *
 * Próximas iteraciones (no v0):
 *   - Conciliación con extracto bancario
 *   - Saldo derivado de boletas + transferencias salientes
 *   - Diferencial cambiario para cuentas USD
 */

const TIPOS = [
  { value: 'MONETARIA', label: 'Monetaria' },
  { value: 'AHORRO', label: 'Ahorro' },
  { value: 'PLAZO_FIJO', label: 'Plazo fijo' },
] as const

const MONEDAS = [
  { value: 'GTQ', label: 'Quetzal (GTQ)' },
  { value: 'USD', label: 'Dólar (USD)' },
] as const

export function NeoCoreBancosPage() {
  const [rows, setRows] = useState<CuentaBancariaPropia[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<CuentaBancariaPropia | null>(null)
  const [creating, setCreating] = useState(false)

  const reload = () => {
    setLoading(true)
    setError(null)
    supabase
      .from('cuenta_bancaria_propia')
      .select('*')
      .order('activa', { ascending: false })
      .order('banco', { ascending: true })
      .then(({ data, error }) => {
        if (error) setError(error.message)
        else setRows((data ?? []) as CuentaBancariaPropia[])
        setLoading(false)
      })
  }

  useEffect(() => { reload() }, [])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5, maxWidth: 640 }}>
            Cuentas bancarias propias de la cooperativa. Acá llegan los depósitos
            de los asociados y desde acá se hacen las transferencias salientes.
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setCreating(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <Plus size={13} weight="bold" />
          Agregar cuenta
        </button>
      </div>

      {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 32, color: 'var(--text-muted)', fontSize: 13 }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div
          style={{
            padding: '40px 24px', background: 'var(--surface)', border: '1px dashed var(--border)',
            borderRadius: 12, textAlign: 'center', color: 'var(--text-muted)',
          }}
        >
          <Bank size={32} weight="duotone" color="var(--n-400)" style={{ marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No hay cuentas bancarias registradas todavía.</p>
          <p style={{ margin: '4px 0 0', fontSize: 12 }}>Agregá la primera para empezar a aceptar depósitos.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {rows.map((r) => (
            <CuentaCard key={r.id} cuenta={r} onEdit={() => setEditing(r)} />
          ))}
        </div>
      )}

      <CuentaBancariaModal
        open={creating || editing !== null}
        cuenta={editing}
        onClose={() => { setCreating(false); setEditing(null) }}
        onSuccess={() => { setCreating(false); setEditing(null); reload() }}
      />
    </div>
  )
}

// =============================================================
// Card de cuenta
// =============================================================

function CuentaCard({ cuenta, onEdit }: { cuenta: CuentaBancariaPropia; onEdit: () => void }) {
  return (
    <div
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '16px 18px',
        display: 'flex', flexDirection: 'column', gap: 10,
        boxShadow: 'var(--shadow-sm)',
        opacity: cuenta.activa ? 1 : 0.6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'var(--brand-50)', color: 'var(--brand-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Bank size={18} weight="duotone" />
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontSize: 14, fontWeight: 700, margin: 0, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {cuenta.alias ?? cuenta.banco}
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0' }}>
              {cuenta.banco}
            </p>
          </div>
        </div>
        {!cuenta.activa && (
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: 'var(--n-100)', color: 'var(--n-600)' }}>
            INACTIVA
          </span>
        )}
      </div>

      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Row label="N° cuenta" value={cuenta.numero_cuenta} mono />
        <Row label="Tipo" value={prettyTipo(cuenta.tipo)} />
        <Row label="Moneda" value={cuenta.moneda} />
      </div>

      <button
        type="button"
        onClick={onEdit}
        style={{
          all: 'unset', cursor: 'pointer',
          alignSelf: 'flex-end',
          fontSize: 11, fontWeight: 600, color: 'var(--brand-600)',
          padding: '4px 8px', borderRadius: 6, marginTop: 2,
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
      >
        <PencilSimple size={11} weight="bold" />
        Editar
      </button>
    </div>
  )
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>
        {value}
      </span>
    </div>
  )
}

function prettyTipo(t: string): string {
  return TIPOS.find((x) => x.value === t)?.label ?? t
}

// =============================================================
// Modal Agregar / Editar
// =============================================================

function CuentaBancariaModal({
  open, cuenta, onClose, onSuccess,
}: {
  open: boolean
  cuenta: CuentaBancariaPropia | null
  onClose: () => void
  onSuccess: () => void
}) {
  const [banco, setBanco] = useState('')
  const [tipo, setTipo] = useState<typeof TIPOS[number]['value']>('MONETARIA')
  const [moneda, setMoneda] = useState<typeof MONEDAS[number]['value']>('GTQ')
  const [numeroCuenta, setNumeroCuenta] = useState('')
  const [alias, setAlias] = useState('')
  const [activa, setActiva] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEdit = cuenta !== null

  useEffect(() => {
    if (!open) return
    setError(null)
    setBanco(cuenta?.banco ?? '')
    setTipo((cuenta?.tipo as typeof TIPOS[number]['value']) ?? 'MONETARIA')
    setMoneda((cuenta?.moneda as typeof MONEDAS[number]['value']) ?? 'GTQ')
    setNumeroCuenta(cuenta?.numero_cuenta ?? '')
    setAlias(cuenta?.alias ?? '')
    setActiva(cuenta?.activa ?? true)
  }, [open, cuenta])

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!banco.trim()) { setError('Falta el nombre del banco.'); return }
    if (!numeroCuenta.trim()) { setError('Falta el número de cuenta.'); return }

    setSubmitting(true)
    const payload = {
      banco: banco.trim().toUpperCase(),
      tipo,
      moneda,
      numero_cuenta: numeroCuenta.trim(),
      alias: alias.trim() || null,
      activa,
    }

    let resp
    if (isEdit && cuenta) {
      resp = await supabase.from('cuenta_bancaria_propia').update(payload).eq('id', cuenta.id)
    } else {
      resp = await supabase.from('cuenta_bancaria_propia').insert(payload)
    }
    setSubmitting(false)

    if (resp.error) {
      // Detectar conflicto natural unique
      if (resp.error.code === '23505') {
        setError('Ya existe una cuenta con este banco/número/moneda.')
      } else {
        setError(resp.error.message)
      }
      return
    }

    onSuccess()
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Editar cuenta bancaria' : 'Agregar cuenta bancaria'}
      subtitle={isEdit ? cuenta?.alias ?? cuenta?.banco : 'Cuenta propia de la cooperativa'}
      size="md"
      blocking={submitting}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button type="submit" form="cuenta-bancaria-form" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Guardando…' : (isEdit ? 'Guardar cambios' : 'Agregar')}
          </button>
        </>
      }
    >
      <form id="cuenta-bancaria-form" onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {error && <div className="error-banner" style={{ margin: 0 }}>{error}</div>}

        <Field label="Banco">
          <input
            value={banco}
            onChange={(e) => setBanco(e.target.value.toUpperCase())}
            placeholder="BANCO INTERNACIONAL"
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Tipo de cuenta">
            <select value={tipo} onChange={(e) => setTipo(e.target.value as typeof TIPOS[number]['value'])}>
              {TIPOS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Moneda">
            <select value={moneda} onChange={(e) => setMoneda(e.target.value as typeof MONEDAS[number]['value'])}>
              {MONEDAS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="N° de cuenta">
          <input
            value={numeroCuenta}
            onChange={(e) => setNumeroCuenta(e.target.value)}
            placeholder="7101905269"
          />
        </Field>

        <Field label="Alias (opcional)">
          <input
            value={alias}
            onChange={(e) => setAlias(e.target.value)}
            placeholder="BI Monetaria"
          />
          <span style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Nombre corto para identificar la cuenta en menús y reportes.
          </span>
        </Field>

        {isEdit && (
          <label style={{ display: 'flex', gap: 10, padding: '10px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={activa} onChange={(e) => setActiva(e.target.checked)} />
            <div>
              <p style={{ fontSize: 13, fontWeight: 600, margin: 0 }}>Cuenta activa</p>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0', lineHeight: 1.4 }}>
                Las cuentas inactivas no aparecen en los selectores de pago.
              </p>
            </div>
          </label>
        )}
      </form>
    </Modal>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="form-label" style={{ fontSize: 11 }}>{label}</span>
      {children}
    </label>
  )
}

void XIcon
